import { useInfiniteQuery, useQuery } from "@tanstack/react-query";
import { Link, useParams } from "@tanstack/react-router";
import { useState, type ReactElement } from "react";
import { ContentHeader, EmptyState, QueryStatus, StatusBadge } from "@cx/ui";
import type { CashSessionListItem, CashSessionView } from "@cx/contracts";
import { formatBRL } from "@cx/domain";
import { useSession } from "../../session";
import { CASH_MOVEMENT_LABEL, POS_METHOD_LABEL } from "./labels";

const when = (iso: string | null) =>
  iso
    ? new Date(iso).toLocaleString("pt-BR", {
        timeZone: "America/Sao_Paulo",
        dateStyle: "short",
        timeStyle: "short",
      })
    : "—";

type Page = { data: CashSessionListItem[]; nextCursor: string | null };

/** Vendas › Controle de caixa (T3.1, RF-VEN-06): aberturas e fechamentos por terminal, com a diferença do fechamento. */
export function CashSessionsPage(): ReactElement {
  const { companyId, api } = useSession();
  const [status, setStatus] = useState<"" | "open" | "closed">("");
  const list = useInfiniteQuery({
    queryKey: ["pos-sessions", companyId, status],
    initialPageParam: "",
    queryFn: ({ pageParam }) =>
      api<Page>(
        `/pos/sessions?${new URLSearchParams({ ...(status ? { status } : {}), ...(pageParam ? { cursor: pageParam } : {}) })}`,
      ),
    getNextPageParam: (last) => last.nextCursor ?? undefined,
  });
  const rows = list.data?.pages.flatMap((p) => p.data) ?? [];
  return (
    <>
      <ContentHeader
        title="Controle de caixa"
        breadcrumb={["Vendas", "Controle de caixa"]}
        actions={
          <a href="/pdv" className="btn btn-primary">
            Abrir o PDV
          </a>
        }
      />
      <div className="box card">
        <div className="row g-2 p-3 pb-0">
          <div className="col-md-3">
            <label className="visually-hidden" htmlFor="cash-status">
              Situação
            </label>
            <select
              id="cash-status"
              className="form-select"
              value={status}
              onChange={(e) => setStatus(e.target.value as typeof status)}
            >
              <option value="">Todos os caixas</option>
              <option value="open">Abertos</option>
              <option value="closed">Fechados</option>
            </select>
          </div>
        </div>
        <QueryStatus loading={list.isLoading} error={list.error} />
        {list.isSuccess && rows.length === 0 && (
          <EmptyState
            title="Nenhum caixa"
            description="O caixa é aberto no PDV, com o fundo de troco."
            actions={[]}
          />
        )}
        {rows.length > 0 && (
          <div className="table-responsive">
            <table className="table align-middle mb-0" aria-label="Caixas">
              <thead>
                <tr>
                  <th>Caixa</th>
                  <th>Terminal</th>
                  <th>Abertura</th>
                  <th>Fechamento</th>
                  <th className="text-end">Vendas</th>
                  <th className="text-end">Diferença</th>
                  <th>Situação</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => (
                  <tr key={r.id}>
                    <td>
                      <Link to="/vendas/pdv/caixas/$id" params={{ id: r.id }}>
                        nº {r.number}
                      </Link>
                    </td>
                    <td>{r.terminalName}</td>
                    <td>{when(r.openedAt)}</td>
                    <td>{when(r.closedAt)}</td>
                    <td className="text-end">{r.salesCount}</td>
                    <td className={`text-end ${r.differenceCents ? "text-danger" : ""}`}>
                      {r.differenceCents === null ? "—" : formatBRL(r.differenceCents)}
                    </td>
                    <td>
                      <StatusBadge tone={r.status === "open" ? "info" : "secondary"}>
                        {r.status === "open" ? "Aberto" : "Fechado"}
                      </StatusBadge>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        {list.hasNextPage && (
          <div className="p-3">
            <button
              type="button"
              className="btn btn-outline-secondary btn-sm"
              onClick={() => list.fetchNextPage()}
            >
              Carregar mais
            </button>
          </div>
        )}
      </div>
    </>
  );
}

/** Caixa: movimentos (vendas, troco, suprimento, sangria, devoluções) e, fechado, a conferência por forma. */
export function CashSessionPage(): ReactElement {
  const { id } = useParams({ from: "/vendas/pdv/caixas/$id" });
  const { companyId, api } = useSession();
  const q = useQuery({
    queryKey: ["pos-sessions", companyId, "view", id],
    queryFn: () => api<CashSessionView>(`/pos/sessions/${id}`),
  });
  const s = q.data;
  return (
    <>
      <ContentHeader
        title={s ? `Caixa nº ${s.number}` : "Caixa"}
        breadcrumb={["Vendas", "Controle de caixa", s ? `nº ${s.number}` : "…"]}
      />
      <QueryStatus loading={q.isLoading} error={q.error} />
      {s && (
        <div className="row g-3">
          <div className="col-lg-4">
            <div className="box card p-3">
              <dl className="row mb-0">
                <dt className="col-6 fw-normal">Terminal</dt>
                <dd className="col-6 text-end">{s.terminalName}</dd>
                <dt className="col-6 fw-normal">Abertura</dt>
                <dd className="col-6 text-end">{when(s.openedAt)}</dd>
                <dt className="col-6 fw-normal">Fundo de troco</dt>
                <dd className="col-6 text-end">{formatBRL(s.openingCents)}</dd>
                <dt className="col-6 fw-normal">Fechamento</dt>
                <dd className="col-6 text-end">{when(s.closedAt)}</dd>
                <dt className="col-6 fw-normal">Vendas</dt>
                <dd className="col-6 text-end">{s.salesCount}</dd>
              </dl>
            </div>
            {s.lines ? (
              <div className="box card p-3 mt-3">
                <h2 className="h6">Conferência</h2>
                <table className="table table-sm mb-0" aria-label="Conferência do caixa">
                  <thead>
                    <tr>
                      <th>Forma</th>
                      <th className="text-end">Esperado</th>
                      <th className="text-end">Contado</th>
                      <th className="text-end">Diferença</th>
                    </tr>
                  </thead>
                  <tbody>
                    {s.lines.map((l) => (
                      <tr key={l.method}>
                        <td>{POS_METHOD_LABEL[l.method]}</td>
                        <td className="text-end">{formatBRL(l.expectedCents)}</td>
                        <td className="text-end">
                          {s.status === "closed" ? formatBRL(l.countedCents) : "—"}
                        </td>
                        <td className={`text-end ${l.differenceCents ? "text-danger" : ""}`}>
                          {s.status === "closed" ? formatBRL(l.differenceCents) : "—"}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <div className="alert alert-info mt-3 small">
                Fechamento cego: o esperado aparece depois de fechar o caixa.
              </div>
            )}
          </div>
          <div className="col-lg-8">
            <div className="box card">
              <div className="table-responsive">
                <table className="table align-middle mb-0" aria-label="Movimentos do caixa">
                  <thead>
                    <tr>
                      <th>Hora</th>
                      <th>Movimento</th>
                      <th>Forma</th>
                      <th className="text-end">Valor</th>
                      <th>Observação</th>
                    </tr>
                  </thead>
                  <tbody>
                    {s.movements.map((m) => (
                      <tr key={m.id}>
                        <td className="text-nowrap">{when(m.createdAt)}</td>
                        <td>
                          {m.originType === "pos_sale" && m.originId ? (
                            <Link to="/vendas/pdv/$id" params={{ id: m.originId }}>
                              {CASH_MOVEMENT_LABEL[m.kind]}
                            </Link>
                          ) : (
                            CASH_MOVEMENT_LABEL[m.kind]
                          )}
                        </td>
                        <td>{POS_METHOD_LABEL[m.method]}</td>
                        <td className={`text-end ${m.amountCents < 0 ? "text-danger" : ""}`}>
                          {formatBRL(m.amountCents)}
                        </td>
                        <td className="small text-muted">{m.notes}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
